import React, { useState, useEffect, useMemo, useRef } from 'react'; // Ajout de useRef
import { useNavigate } from 'react-router-dom';
import SearchInputComponent from '../shared/SearchInputComponent';
import {
  Container,
  ContentWrapper,
  ReturnButton,
  SubmitButton as OriginalSubmitButton,
  OptionsContainer,
  LoadingOverlay,
  ChatBubbleContainer,
  ChatText,
  LogoImage,
  LogoBubbleWrapper,
  OptionIcon,
  OptionButton
} from './styles';

import compassImage from '../../assets/meo_write.png';
import ProgressBar from '../ProgressBar';
import useDeviceType from '../useDeviceType';
import apiCall from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import theme from '../../theme';
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png';
import { parseFormacodeData, searchFormacodes } from '../../utils/formaCodeUtils';
import { domainIconsFormacodesSousGrandDomaines } from './domainIconMap';
import defaultDomainIcon from '../../assets/point-dinterrogation-dessiner.png';

const BrowserAndSelectedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 15px;
`;

const OptionsScrollContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(var(--vh, 1vh) * 50);
  overflow-y: auto;
  padding-right: 5px;
  
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.mediumGray} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.mediumGray};
    border-radius: 3px;
  }
`;

const StyledSubmitButton = styled(OriginalSubmitButton)`
  // Styles spécifiques si nécessaires, sinon il hérite déjà.
`;

// Nouveau bouton stylisé pour "Voir plus"
const ShowMoreButton = styled(OptionButton)`
  background-color: ${theme.colors.secondary}; // Couleur distincte
  color: white;
  text-align: center;
  justify-content: center; // Centre le texte si OptionButton utilise flex
  width: calc(100% - 20px); // S'assurer qu'il prend la largeur comme les autres options
  margin: 10px auto; // Espacement

  &:hover {
    background-color: ${theme.colors.secondaryDark};
  }
  &:disabled {
    background-color: ${theme.colors.mediumGray};
    cursor: not-allowed;
  }
`;

const SEARCH_RESULTS_BATCH_SIZE = 15; // Constante pour la pagination

const DomainSelection = () => {
  const navigate = useNavigate();
  const url = window.location.href;
  const modifyProfile = url.includes('profile/modify');
  const { isMobile } = useDeviceType();
  const { checkAuth } = useAuth();

  const [selectedDomains, setSelectedDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(true);
  const [text, setText] = useState('');
  const fullText = "Choisis les domaines qui t'intéressent !";
  const [searchText, setSearchText] = useState('');
  const [submitActive, setSubmitActive] = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  
  const [formacodeData, setFormacodeData] = useState({ formacodes: [], hierarchy: [] });
  const [dataLoadingError, setDataLoadingError] = useState(null);

  const [commonDomainsWithDetails, setCommonDomainsWithDetails] = useState([]);
  const [hierarchyCodeToIconMap, setHierarchyCodeToIconMap] = useState(new Map());

  // State pour la pagination des résultats de recherche
  const [displayedSearchResultsCount, setDisplayedSearchResultsCount] = useState(SEARCH_RESULTS_BATCH_SIZE);
  const scrollContainerRef = useRef(null); // Référence pour le conteneur scrollable


  useEffect(() => {
    const loadFormacodeData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/formacodes_recoded_optimized.csv');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const csvData = await response.text();
        if (!csvData || csvData.trim() === "") throw new Error("CSV vide");
        
        const parsed = parseFormacodeData(csvData);
        setFormacodeData(parsed);
        setDataLoadingError(null);
      } catch (error) {
        console.error('Erreur chargement formacode:', error);
        setDataLoadingError(error.message);
        setFormacodeData({ formacodes: [], hierarchy: [] });
      } 
    };
    loadFormacodeData();
  }, []);

  useEffect(() => {
    if (formacodeData.formacodes.length > 0 && Object.keys(domainIconsFormacodesSousGrandDomaines).length > 0) {
      const newCommonDomains = [];
      const newHierarchyMap = new Map();

      Object.entries(domainIconsFormacodesSousGrandDomaines).forEach(([domainName, icon]) => {
        let descriptorToFind = domainName;
        if (domainName === "Entrepreneuriat - Création d'entreprise") {
          descriptorToFind = "Création entreprise";
        }
        
        const formacodeEntry = formacodeData.formacodes.find(
          fc => fc.descriptorRich === descriptorToFind && 
                fc.hierarchyCode && 
                (fc.hierarchyCode.length === 4 || descriptorToFind === "Création entreprise")
        );
        if (formacodeEntry) {
          newCommonDomains.push({
            name: domainName,
            code: formacodeEntry.code,
            icon: icon,
          });
          if (formacodeEntry.hierarchyCode) {
            newHierarchyMap.set(formacodeEntry.hierarchyCode, icon);
          }
        }
      });
      newCommonDomains.sort((a, b) => a.name.localeCompare(b.name));
      setCommonDomainsWithDetails(newCommonDomains);
      setHierarchyCodeToIconMap(newHierarchyMap);
      setIsLoading(false);
    } else if (formacodeData.formacodes.length > 0) {
      console.warn("Formacodes chargés, mais domainIconsFormacodesSousGrandDomaines est vide ou non disponible pour préparer les domaines communs.");
      setCommonDomainsWithDetails([]);
      setHierarchyCodeToIconMap(new Map());
      setIsLoading(false);
    } else if (!isLoading && !dataLoadingError) {
        setIsLoading(false);
    }
  }, [formacodeData, dataLoadingError, isLoading]); // Ajout de isLoading pour le cas CSV vide

  // Effet pour scroller en haut et réinitialiser la pagination lors d'une nouvelle recherche
  useEffect(() => {
    if (searchText.trim() && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
      setDisplayedSearchResultsCount(SEARCH_RESULTS_BATCH_SIZE); // Réinitialise le compteur
    }
  }, [searchText]);


  useEffect(() => {
    if (modifyProfile) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.sub_domaine_formation && Array.isArray(userData.sub_domaine_formation)) {
         // Si on est en modification, on pourrait pré-remplir selectedDomains,
         // mais le comportement actuel (clear et re-sélection) est aussi valide.
         // Pour pré-remplir:
         // setSelectedDomains(userData.sub_domaine_formation);
         // Pour garder le comportement de clear:
        setSelectedDomains([]); // Ou ne rien faire pour laisser l'utilisateur re-sélectionner
      }
    }
  }, [modifyProfile]);

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 15);
    return () => clearInterval(typingInterval);
  }, [fullText]); // Ajout de fullText en dépendance, même si constant ici

  const searchResults = useMemo(() => {
    if (!searchText.trim()) return [];
    // searchFormacodes devrait retourner un tableau d'objets { formacode: {...}, score: X }
    // ou juste les formacodes. Assurons-nous qu'il retourne bien un tableau de formacodes.
    const results = searchFormacodes(formacodeData.formacodes, searchText);
    console.warn(results); // Pour débogage
    // Si searchFormacodes retourne {formacode: ...}, on extrait le formacode
    return results.map(r => r.formacode ? r.formacode : r);
  }, [searchText, formacodeData.formacodes]);

  // Résultats de recherche paginés
  const paginatedSearchResults = useMemo(() => {
    return searchResults.slice(0, displayedSearchResultsCount);
  }, [searchResults, displayedSearchResultsCount]);


  const getIconForSearchResult = (formacode) => {
    if (!formacode) return defaultDomainIcon;
    const directCommonDomain = commonDomainsWithDetails.find(cd => cd.code === formacode.code);
    if (directCommonDomain && directCommonDomain.icon) return directCommonDomain.icon;
    if (formacode.hierarchyCode) {
      if (formacode.hierarchyCode.length === 4) {
        const icon = hierarchyCodeToIconMap.get(formacode.hierarchyCode);
        if (icon) return icon;
      } else if (formacode.hierarchyCode.length > 4) {
        const parentHierarchyCode = formacode.hierarchyCode.substring(0, 4);
        const icon = hierarchyCodeToIconMap.get(parentHierarchyCode);
        if (icon) return icon;
      }
    }
    return defaultDomainIcon;
  };

  const toggleDomainSelection = (domainCode) => {
    setSelectedDomains((prev) => {
      const uniquePrev = Array.from(new Set(prev)); // Assurer l'unicité avant d'opérer
      if (uniquePrev.includes(domainCode)) {
        return uniquePrev.filter(item => item !== domainCode);
      } else {
        return [...uniquePrev, domainCode];
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedDomains.length === 0 || isTyping) return;
    
    const uniqueSelectedDomains = Array.from(new Set(selectedDomains));
    if (uniqueSelectedDomains.length !== selectedDomains.length) {
        setSelectedDomains(uniqueSelectedDomains); 
    }

    setIsLoading(true);
    try {
      const domainsToSubmit = uniqueSelectedDomains.map(domainCode => {
        const formacodeEntry = formacodeData.formacodes.find(fc => fc.code === domainCode);
        return formacodeEntry?.hierarchyCode || domainCode; // Utilise hierarchyCode si disponible
      });

      if (modifyProfile) {

        await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', {
          sub_domaine_formation: domainsToSubmit // Envoyer les hierarchyCodes
        });
        let userData = JSON.parse(localStorage.getItem('user') || '{}');
        // MODIFICATION ICI: Stocker les hierarchyCodes (ou codes fallback) dans sub_domaine_formation
        userData.sub_domaine_formation = domainsToSubmit; 
        // userData.domaine_formation = []; // Si on veut écraser aussi les domaines principaux
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('need_update_ranking', 'true');
        localStorage.setItem('need_update_ranking_formation', 'true');
        if(domainsToSubmit.length === 1) {
            await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', {
              domaine_formation: domainsToSubmit // Send specific favorite codes
            });
            let userData = JSON.parse(localStorage.getItem('user'));
            userData.domaine_formation = domainsToSubmit;
            localStorage.setItem('user', JSON.stringify(userData));
            await checkAuth();
            navigate('/profile');
            return;
        }
        await checkAuth();
        // Pour la modification de profil, on navigue toujours vers la sélection des principaux
        // car l'utilisateur modifie explicitement cette section.
        navigate('/profile/modify/domaines-principaux');
      } else {
        // Flux normal (pas de modification de profil)
        localStorage.setItem('sub_domaine_formation', JSON.stringify(domainsToSubmit));

        // MODIFICATION ICI: Gérer le cas d'un seul domaine sélectionné
        if (domainsToSubmit.length === 1) {
          // Si un seul domaine est soumis, il devient automatiquement le domaine principal
          // et on saute l'étape DomainSelectionPrincipaux.
          localStorage.setItem('domaines_principaux', JSON.stringify(domainsToSubmit)); 
          navigate('/lycee'); // Naviguer directement à l'étape suivante (par exemple, '/lycee')
        } else {
          // Si plusieurs domaines sont soumis (ou aucun, bien que déjà géré par le disabled du bouton),
          // naviguer vers la sélection des principaux.
          navigate('/domaines-principaux');
        }
      }
    } catch (error) {
      console.error('Error saving domains:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => navigate(modifyProfile ? '/profile' : "/general-moyenne");

  const handleSubmitPressStart = () => {
    if (isLoading || isTyping || selectedDomains.length === 0) return;
    setSubmitActive(true);
  };

  const handleSubmitPressEnd = () => {
    if (isLoading || isTyping || selectedDomains.length === 0) return;
    setSubmitActive(false);
    setTimeout(handleSubmit, 10);
  };
  
  const handleOptionPressStart = (code) => setActiveOption(code);
  const handleOptionPressEnd = () => setActiveOption(null);

  const handleShowMoreResults = () => {
    setDisplayedSearchResultsCount(prevCount => prevCount + SEARCH_RESULTS_BATCH_SIZE);
  };

  if (dataLoadingError && !isLoading) { // Afficher l'erreur seulement si le chargement initial est terminé
    return (
      <Container>
        <ContentWrapper>
          <p style={{color: 'red', textAlign: 'center', padding: '20px'}}>
            Erreur: {dataLoadingError}. Veuillez réessayer.
          </p>
          <ReturnButton onClick={handleBack} style={{ margin: '20px auto', display: 'block' }}>
            Retour
          </ReturnButton>
        </ContentWrapper>
      </Container>
    );
  }
  
  const showSearchResults = searchText.trim() !== '';

  return (
    <Container>
      {/* Affiché si isLoading global est true ET qu'il n'y a pas d'erreur de data ET que ce n'est pas le submit qui charge */}
      {isLoading && !dataLoadingError && !submitActive && <LoadingOverlay isLoading={true} />}
      <ReturnButton onClick={handleBack}>
        <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px' }} />
      </ReturnButton>
      <ContentWrapper>
        {!modifyProfile && <ProgressBar startPercent={50} endPercent={60} />}
        <LogoBubbleWrapper>
          <LogoImage src={compassImage} alt="Compass Logo" />
          <ChatBubbleContainer isMobile={isMobile}>
            <ChatText isVisible={text.length > 0}><span>{text}</span></ChatText>
          </ChatBubbleContainer>
        </LogoBubbleWrapper>

        <BrowserAndSelectedContainer>
          <SearchInputComponent
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Rechercher un domaine..."
          />
        </BrowserAndSelectedContainer>

        {/* Le conteneur de scroll reçoit la réf */}
        <OptionsScrollContainer ref={scrollContainerRef} isMobile={isMobile}>
          {isLoading && !dataLoadingError && formacodeData.formacodes.length === 0 ? ( // Condition pour le chargement initial des données
             <div style={{ textAlign: 'center', padding: '20px', color: theme.colors.textLighter }}>Chargement des domaines...</div>
          ) : (
            <OptionsContainer style={{ paddingBottom: '10px' }}>
              {showSearchResults ? (
                <>
                  {paginatedSearchResults.length > 0 ? paginatedSearchResults.map((formacodeItem) => ( // formacodeItem peut être null si searchResults contient des trous
                    formacodeItem && // Vérification que formacodeItem n'est pas null/undefined
                    <OptionButton
                      key={formacodeItem.code}
                      $isSelected={selectedDomains.includes(formacodeItem.code)}
                      onClick={() => toggleDomainSelection(formacodeItem.code)}
                      active={activeOption === formacodeItem.code}
                      onMouseDown={() => handleOptionPressStart(formacodeItem.code)}
                      onMouseUp={handleOptionPressEnd}
                      onMouseLeave={handleOptionPressEnd}
                      onTouchStart={() => handleOptionPressStart(formacodeItem.code)}
                      onTouchEnd={handleOptionPressEnd}
                      disabled={isTyping}
                    >
                      <OptionIcon 
                        src={getIconForSearchResult(formacodeItem)} 
                        alt=""
                        $isSelected={selectedDomains.includes(formacodeItem.code)}
                      />
                      {formacodeItem.descriptorRich}
                    </OptionButton>
                  )) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: theme.colors.textLighter }}>
                      Aucun résultat pour "{searchText}"
                    </div>
                  )}
                  {searchResults.length > displayedSearchResultsCount && (
                    <ShowMoreButton onClick={handleShowMoreResults} disabled={isTyping}>
                      Voir plus ({searchResults.length - displayedSearchResultsCount} de plus)
                    </ShowMoreButton>
                  )}
                </>
              ) : ( // Afficher les domaines communs si la recherche est vide
                commonDomainsWithDetails.length > 0 ? commonDomainsWithDetails.map(domain => (
                  <OptionButton
                    key={domain.code}
                    $isSelected={selectedDomains.includes(domain.code)}
                    onClick={() => toggleDomainSelection(domain.code)}
                    active={activeOption === domain.code}
                    onMouseDown={() => handleOptionPressStart(domain.code)}
                    onMouseUp={handleOptionPressEnd}
                    onMouseLeave={handleOptionPressEnd}
                    onTouchStart={() => handleOptionPressStart(domain.code)}
                    onTouchEnd={handleOptionPressEnd}
                    disabled={isTyping}
                  >
                    <OptionIcon 
                      src={domain.icon || defaultDomainIcon} 
                      alt=""
                      $isSelected={selectedDomains.includes(domain.code)}
                    />
                    {domain.name}
                  </OptionButton>
                )) : (
                  // Si pas de recherche et pas de domaines communs (ou en cours de chargement initial non géré par le isLoading principal)
                  !isLoading && // N'afficher que si le chargement principal est terminé
                  <div style={{ textAlign: 'center', padding: '20px', color: theme.colors.textLighter }}>
                    Aucun domaine suggéré. Utilisez la recherche.
                  </div>
                )
              )}
            </OptionsContainer>
          )}
        </OptionsScrollContainer>

        <StyledSubmitButton
          type="button"
          disabled={isTyping || selectedDomains.length === 0 || (isLoading && submitActive)} // Désactivé si envoi en cours
          style={{ 
            opacity: (isTyping || selectedDomains.length === 0 || (isLoading && submitActive)) ? 0.5 : 1,
            cursor: (isTyping || selectedDomains.length === 0 || (isLoading && submitActive)) ? 'not-allowed' : 'pointer',
          }}
          active={submitActive}
          onMouseDown={handleSubmitPressStart}
          onMouseUp={handleSubmitPressEnd}
          onMouseLeave={() => setSubmitActive(false)}
          onTouchStart={handleSubmitPressStart}
          onTouchEnd={handleSubmitPressEnd}
        >
          {(isLoading && submitActive) ? 'Envoi...' : `Continuer (${selectedDomains.length})`}
        </StyledSubmitButton>
      </ContentWrapper>
    </Container>
  );
};

export default DomainSelection;